import { useEffect, useMemo, useState } from "react";
import { Grid, Segment, Header, Button, Form, Icon, Label, Checkbox } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import LinktreePreview from "./LinktreePreview";
import backgroundImage from "./images/linktree-bg.webp";
import logoImage from "./images/linktree-logo.png";
import LinktreeLinksDraggable from "./linktreeLinksDraggable";

const LinktreeView = ({ show, data, onClickDelete, onClickCancel, onClickSubmit, onSortLinks, onClickDeleteLinks, onToggleActiveLink }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState();

  useEffect(() => {
    if (data) {
      setFormData({
        ...data,
      });
    }
  }, [data]);

  const sortedLinks = useMemo(() => {
    if (!data || !Array.isArray(data.links)) return [];

    return [...data.links].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );
  }, [data]);

  if (!show || !data) return null;

  const handleOnChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <section>
      <Grid stackable>
        <Grid.Column width={10}>
          <Segment basic color="blue" padded="very">
            <Header as="h3">{t(`headline-general`)}</Header>
            <Form>
              <Form.Field>
                <Checkbox
                  toggle
                  checked={!!formData?.isActive}
                  label={t("input-label-isActive")}
                  onChange={(e, { checked }) =>
                    setFormData(prev => ({ ...prev, isActive: checked ? 1 : 0 }))
                  }
                />
              </Form.Field>
              <Form.Input
                id="title"
                label={t("input-label-title")}
                placeholder={t("input-placeholder-title")}
                value={formData?.title || ""}
                onChange={handleOnChange}
              />
              <Form.Input
                id="subtitle"
                label={t("input-label-subtitle")}
                placeholder={t("input-placeholder-subtitle")}
                value={formData?.subtitle || ""}
                onChange={handleOnChange}
              />
              <Form.Field>
                <label>{t("label-slug")}</label>
                <p>
                  <Icon name="linkify" />
                  <span
                    style={{
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      direction: "rtl",
                      textAlign: "left",
                      title: `https://bhmm.de/l/${data.slug}`,
                    }}
                  >/l/{data.slug}</span>
                </p>
              </Form.Field>
              <Form.Field>
                <Label color="blue">
                  <Icon name="tag" />
                  {data.type}
                </Label>
              </Form.Field>
            </Form>
          </Segment>
          <Segment basic color="blue" padded="very">
            <Header as="h3">{t("headline-links")}</Header>

            {(!data.links || data.links.length === 0) ? (
              <p style={{ opacity: 0.6 }}>
                {t("hint-links-placeholder")}
              </p>
            ) : (
              <LinktreeLinksDraggable
                links={sortedLinks}
                onToggleActive={({ id, isActive }) => {
                  onToggleActiveLink({ id, isActive });
                }}
                onClickDelete={({ id }) => {
                  onClickDeleteLinks(id);
                }}
                onOrderChange={(newOrder) => {
                  onSortLinks(newOrder);
                }}
              />
            )}
          </Segment>
        </Grid.Column>
        <Grid.Column width={6}>
          <Segment basic color="blue" padded="very">
            <Header as="h3">{t(`headline-preview`)}</Header>

            {data ? (
              <LinktreePreview
                title={data.title}
                subtitle={data.subtitle}
                backgroundImage={backgroundImage}
                logoImage={logoImage}
                links={data.links || []}
              />
            ) : (
              <div
                style={{
                  minHeight: "400px",
                  border: "1px dashed rgba(0,0,0,0.2)",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.5,
                }}
              >
                {t("hint-preview-placeholder")}
              </div>
            )}
          </Segment>
        </Grid.Column>
      </Grid>
      <Grid>
        <Grid.Column>
          <Button
            floated="left"
            size="medium"
            onClick={() => onClickDelete({ id: data.id })}
          >
            <Icon name="trash" />
            {t("button-delete")}
          </Button>
          <Button
            floated="right"
            primary
            size="medium"
            onClick={() => onClickSubmit({ id: data.id, data: formData })}
          >
            <Icon name="check" />
            {t("button-save")}
          </Button>
          <Button
            floated="right"
            size="medium"
            onClick={onClickCancel}
          >
            <Icon name="close" />
            {t("button-cancel")}
          </Button>
        </Grid.Column>
      </Grid>
    </section>
  );
};

export default LinktreeView;
